import { memo, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import type { Chart as IChart, LineChart, BarChart, PieChart, Line } from '@trinity/chat';
import { BrandColorCategory, ColorPaletteType } from '@trinity/chat';
import { NivoBarChart } from '@trinity/chat-components/components/Widgets/Charts/Bar';
import { NivoDoughnutChart } from '@trinity/chat-components/components/Widgets/Charts/Doughnut';
import { ChartLegend } from '@trinity/chat-components/components/Widgets/Charts/Legend';
import { NivoLineChart } from '@trinity/chat-components/components/Widgets/Charts/Line';
import { NivoPieChart } from '@trinity/chat-components/components/Widgets/Charts/Pie';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Widgets/Charts/StackedBar';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context';
import { BrandThemingContext } from '@trinity/chat-components/containers/Branding/Context';
import { DEFAULT_COLORS } from '@trinity/chat-components/lib/chart-colors';

type Props = {
  item: IChart['chart'];
  type: IChart['type'];
};

export const Chart = memo((props: Props) => {
  const { colors, ready } = useChartColors();

  const legend = [];

  const renderChart = (chartType: string) => {
    switch (chartType) {
      case 'stacked-bar': {
        const chart = props.item as BarChart;
        for (let i = 0; i < chart.data.length; i++) {
          legend.push({ key: chart.data[i].x, color: colors[i] });
        }
        return <NivoStackedBarChart chart={chart} colors={colors} />;
      }

      case 'bar': {
        const chart = props.item as BarChart;
        for (let i = 0; i < chart.data.length; i++) {
          legend.push({ key: chart.data[i].x, color: colors[i] });
        }
        return <NivoBarChart chart={chart} colors={colors} />;
      }

      case 'pie': {
        const chart = props.item as PieChart;
        for (let i = 0; i < chart.data.length; i++) {
          legend.push({ key: chart.data[i].label, color: colors[i] });
        }
        return <NivoPieChart chart={chart} colors={colors} />;
      }
      case 'line': {
        const chart = props.item as LineChart;

        if (Array.isArray(chart.data)) {
          for (let i = 0; i < chart.data.length; i++) {
            const line = chart.data[i];
            legend.push({ key: line.label ?? line.id, color: colors[i] });
          }
        } else {
          const d = chart.data as Line;
          legend.push({ key: d.label ?? d.id, color: colors[0] });
        }
        return <NivoLineChart chart={chart} colors={colors} />;
      }

      case 'doughnut': {
        const chart = props.item as PieChart;
        for (let i = 0; i < chart.data.length; i++) {
          legend.push({ key: chart.data[i].label, color: colors[i] });
        }
        return <NivoDoughnutChart chart={chart} colors={colors} />;
      }

      default:
        return null;
    }
  };

  return (
    <div>
      <ChartRoot>
        {(ready && props.item) &&
          <>
            <Title>{props.item.title}</Title>
            <ChartArea>{renderChart(props.type)}</ChartArea>
            {legend.length > 0 && <ChartLegend legend={legend} />}
          </>}
      </ChartRoot>
    </div>
  );
});

const ChartRoot = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--gray-l);
  border-radius: 15px;
  padding: 12px;
  margin-top: 10px;
  background-color: white;
`;

const ChartArea = styled.div`
  height: 320px;
  width: 100%;
  position: relative;
`;

const Title = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
}));

const useChartColors = () => {
  const branding = useContext(BrandThemingContext);
  const theming = useContext(AppThemingContext);

  const brandingData = branding?.query?.data;

  const ready = useMemo((): boolean => {
    const brandingReady = !branding?.query?.isLoading && Boolean(branding?.query?.isFetchedAfterMount);
    const themingReady = theming == null || Boolean(theming.query?.isFetchedAfterMount);
    return brandingReady && themingReady;
  }, [
    branding?.query?.isFetchedAfterMount,
    branding?.query?.isLoading,
    theming,
  ]);

  const colors = useMemo(() => {
    if (!brandingData) return DEFAULT_COLORS;

    if (theming?.query?.data?.enabled) {
      const paletteId = brandingData.theming.themes.find(x => x.categoryId === BrandColorCategory.Options)?.paletteId;
      return brandingData.theming.palettes.find(x => x.id === paletteId)?.data?.colors ?? DEFAULT_COLORS;
    }

    return brandingData.theming.palettes.find(x => x.typeId === ColorPaletteType.Preset && x.categoryId === BrandColorCategory.Options)?.data?.colors ?? DEFAULT_COLORS;
  }, [theming?.query?.data?.enabled, brandingData]);

  return {
    ready: ready || !brandingData,
    colors,
  };
};
